import React from "react";
import styled, { css } from "styled-components";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

import UserInfo from "./components/UserInfo";
import { ThemeToggle } from "../../../../design-system";
import { IconKanban } from "../../../../design-system/icons/icons";

const ToolbarRoot = styled.div`
  height: 56px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-6);
  gap: var(--space-4);
`;

const LeftSide = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const brandRowStyles = css`
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  padding: 4px 8px 4px 4px;
  border-radius: var(--radius-md);
  text-decoration: none;
  color: inherit;
  transition: background var(--duration-fast) var(--ease-standard);
  min-width: 0;
`;

const BrandLink = styled.a`
  ${brandRowStyles}

  &:hover {
    background: var(--color-surface-subtle);
    text-decoration: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const BrandStatic = styled.div`
  ${brandRowStyles}
`;

const LogoImg = styled.img`
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  object-fit: cover;
  background: var(--color-surface-subtle);
  flex-shrink: 0;
  display: block;
`;

const LogoLetter = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  background: var(--color-accent);
  color: var(--color-text-inverse);
  font-family: var(--font-display);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
  flex-shrink: 0;
  letter-spacing: -0.02em;
  text-transform: uppercase;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Title = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
`;

const Subtitle = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
`;

const ExternalIcon = styled.svg`
  color: var(--color-text-tertiary);
  flex-shrink: 0;
`;

const RightSide = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
`;

// Matches ThemeToggle's icon-button footprint so the corner controls line up.
const BoardLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  transition: background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface-subtle);
    color: var(--color-text);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const ExternalLinkIcon = () => (
  <ExternalIcon width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </ExternalIcon>
);

type Props = {
  community: any;
  user: any;
};

const HeaderBar = ({ community, user }: Props) => {
  const communityTitle =
    community?.title?.trim() || user?.communityId || "";
  const communityId = user?.communityId || community?.id || "";
  const logoURL = community?.logoURL || "";
  const initial = (communityTitle.charAt(0) || "C").toUpperCase();
  // Opens this community's public landing page on tribed.io. The landing slug
  // defaults to the community id when no custom slug is set.
  const landingSlug =
    (community?.slug && String(community.slug).trim()) || communityId;
  const landingUrl = landingSlug
    ? `https://tribed.io/c/${encodeURIComponent(landingSlug)}`
    : "";

  const logo = logoURL ? (
    <LogoImg src={logoURL} alt={communityTitle || "Community logo"} />
  ) : (
    <LogoLetter>{initial}</LogoLetter>
  );

  const titleNode = (
    <TitleBlock>
      <Title>
        {communityTitle || "Community"}
        {landingUrl && <ExternalLinkIcon />}
      </Title>
      {landingUrl && <Subtitle>Open landing page</Subtitle>}
    </TitleBlock>
  );

  return (
    <ToolbarRoot>
      <LeftSide>
        {landingUrl ? (
          <BrandLink
            href={landingUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Open public landing page"
          >
            {logo}
            {titleNode}
          </BrandLink>
        ) : (
          <BrandStatic>
            {logo}
            {titleNode}
          </BrandStatic>
        )}
      </LeftSide>
      <RightSide>
        <BoardLink to="/board" title="Issue board" aria-label="Issue board">
          <IconKanban />
        </BoardLink>
        <ThemeToggle />
        <UserInfo />
      </RightSide>
    </ToolbarRoot>
  );
};

const mapStateToProps = (state) => ({
  community: state.community,
  user: state.storeUser,
});

export default connect(mapStateToProps, null)(HeaderBar);
